/**
 * 应用入口: 注册主题 → Alpine store/页面组件 → 启动 → 恢复登录态.
 * 约定: 跨页面状态放 stores, 页面级状态放 pages/*, 可复用交互放 components/*.
 */

import Alpine from 'alpinejs'

import './styles/main.css'
import { applyLocale, i18nStore, resolveInitialLocale } from './lib/i18n'
import { observeIcons, renderIcons } from './lib/icons'
import { initSwipeBack } from './lib/swipe-back'
import { applyTheme, resolveInitialTheme } from './lib/theme'
import { loginPage, registerPage } from './pages/auth'
import { coursesPage } from './pages/courses'
import { dictionaryPage } from './pages/dictionary'
import { favoritesPage } from './pages/favorites'
import { homePage } from './pages/home'
import { practicePage } from './pages/practice'
import { AUTH_EXPIRED_EVENT } from './lib/api'
import { profilePage } from './pages/profile'
import { membershipPage, ordersPage } from './pages/membership'
import { redeemPage } from './pages/redeem'
import { authStore, type AuthStore } from './stores/auth'
import { goBack, routerStore, type RouterStore } from './stores/router'
import { toastStore } from './stores/toast'
import { uiStore } from './stores/ui'
import { wordCardStore } from './stores/word-card'

// 1. 先落地主题与界面语言, 避免首屏闪烁 (语言 key 与 index.html 内联脚本一致)
applyLocale(resolveInitialLocale())
applyTheme(resolveInitialTheme())

// 2. 注册 store (认证 / 路由 / 主题 / 提示)
// 注意: 必须通过 Alpine.store(name) 拿响应式实例再调方法,
// 直接调 factory 返回的原始对象不会触发 Alpine 重渲染(登录态恢复会卡在启动占位)
Alpine.store('router', routerStore())
Alpine.store('auth', authStore())
Alpine.store('ui', uiStore())
Alpine.store('i18n', i18nStore())
Alpine.store('toast', toastStore())
Alpine.store('wordCard', wordCardStore())

// 3. 注册页面与组件
Alpine.data('loginPage', loginPage)
Alpine.data('registerPage', registerPage)
Alpine.data('redeemPage', redeemPage)
Alpine.data('membershipPage', membershipPage)
Alpine.data('ordersPage', ordersPage)
Alpine.data('profilePage', profilePage)
Alpine.data('homePage', homePage)
Alpine.data('coursesPage', coursesPage)
Alpine.data('dictionaryPage', dictionaryPage)
Alpine.data('favoritesPage', favoritesPage)
Alpine.data('practicePage', practicePage)

// 4. 方便在浏览器控制台调试
declare global {
  interface Window {
    Alpine: typeof Alpine
  }
}
window.Alpine = Alpine

// 任意受保护接口登录失效时, 同步清理状态并回到登录页.
window.addEventListener(AUTH_EXPIRED_EVENT, () => {
  ;(Alpine.store('auth') as AuthStore).logout()
})

// 渠道返回参数仅恢复订单页面, 不作为到账依据.
const returnedOrder = new URLSearchParams(window.location.search).get('order')
if (returnedOrder && /^ord_[a-f0-9]{32}$/.test(returnedOrder)) {
  history.replaceState(null, '', `${window.location.pathname}#/orders/${returnedOrder}`)
}
Alpine.start()
;(Alpine.store('router') as RouterStore).init()

// 4.1 移动端横滑返回 (与系统原生边缘手势互补)
initSwipeBack(goBack)

// 5. 渲染图标, 并跟随 Alpine 动态插入的节点
renderIcons()
observeIcons()

// 6. 用已保存的令牌恢复登录态 (失败会自动回到登录页)
void (Alpine.store('auth') as AuthStore).bootstrap()
